// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

import { SEED_ORG_PLACEHOLDER, SEED_WORKSPACE_PLACEHOLDER } from './constants'
import type { SeedSchema } from './constants'

/**
 * Seed schema for the proposals collection.
 * Agent work requests that flow through the gate before becoming missions.
 */
export const proposalsSchema: SeedSchema = {
  name: 'proposals',
  title: 'Proposals',
  description: 'Agent work requests submitted for gate review before execution',
  category: 'orchestration',
  target: 'records',
  titleField: 'title',
  orgId: SEED_ORG_PLACEHOLDER,
  workspaceId: SEED_WORKSPACE_PLACEHOLDER,
  builtIn: true,
  version: 1,
  archived: false,
  ownerPluginId: '',
  metadata: { idPrefix: 'prps' },
  defaultSort: { field: 'createdAt', direction: 'desc' },
  fields: [
    {
      name: 'agentId',
      label: 'Agent',
      type: 'text',
      required: true,
      order: 1,
      config: { placeholder: 'Agent identifier' },
    },
    {
      name: 'title',
      label: 'Title',
      type: 'text',
      required: true,
      order: 2,
      config: { placeholder: 'Proposal title' },
    },
    {
      name: 'description',
      label: 'Description',
      type: 'richtext',
      required: false,
      order: 3,
      config: { placeholder: 'Detailed proposal description' },
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      required: true,
      defaultValue: 'pending',
      order: 4,
      options: [
        { value: 'pending', label: 'Pending', color: '#f59e0b' },
        { value: 'accepted', label: 'Accepted', color: '#22c55e' },
        { value: 'rejected', label: 'Rejected', color: '#ef4444' },
      ],
      config: { renderer: 'badge' },
    },
    {
      name: 'proposedSteps',
      label: 'Proposed Steps',
      type: 'json',
      required: true,
      order: 5,
      config: { helpText: 'Array of step definitions the agent plans to execute' },
    },
    {
      name: 'gateResults',
      label: 'Gate Results',
      type: 'json',
      required: false,
      order: 6,
      config: { helpText: 'Automated gate evaluation results (scope, risk, policy checks)' },
    },
    {
      name: 'rejectionReason',
      label: 'Rejection Reason',
      type: 'text',
      required: false,
      order: 7,
      config: { placeholder: 'Why the proposal was rejected' },
    },
    {
      name: 'source',
      label: 'Source',
      type: 'select',
      required: false,
      order: 8,
      options: [
        { value: 'agent_initiative', label: 'Agent Initiative', color: '#6366f1' },
        { value: 'trigger', label: 'Trigger', color: '#0ea5e9' },
        { value: 'reaction', label: 'Reaction', color: '#8b5cf6' },
        { value: 'human', label: 'Human', color: '#22c55e' },
      ],
      config: { renderer: 'badge' },
    },
  ],
}

/**
 * Seed schema for the missions collection.
 * Approved proposals become executable missions with tracked lifecycle.
 */
export const missionsSchema: SeedSchema = {
  name: 'missions',
  title: 'Missions',
  description: 'Approved proposals promoted to executable work units with tracked lifecycle',
  category: 'orchestration',
  target: 'records',
  titleField: 'title',
  orgId: SEED_ORG_PLACEHOLDER,
  workspaceId: SEED_WORKSPACE_PLACEHOLDER,
  builtIn: true,
  version: 3,
  archived: false,
  ownerPluginId: '',
  metadata: { idPrefix: 'misn' },
  defaultSort: { field: 'createdAt', direction: 'desc' },
  fields: [
    {
      name: 'title',
      label: 'Title',
      type: 'text',
      required: true,
      order: 1,
      config: { placeholder: 'Mission title' },
    },
    {
      name: 'description',
      label: 'Description',
      type: 'richtext',
      required: false,
      order: 2,
      config: { placeholder: 'Mission description carried from proposal' },
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      required: true,
      defaultValue: 'approved',
      order: 3,
      options: [
        { value: 'approved', label: 'Approved', color: '#3b82f6' },
        { value: 'running', label: 'Running', color: '#f59e0b' },
        { value: 'succeeded', label: 'Succeeded', color: '#22c55e' },
        { value: 'failed', label: 'Failed', color: '#ef4444' },
      ],
      config: { renderer: 'badge' },
    },
    {
      name: 'proposalId',
      label: 'Proposal',
      type: 'text',
      required: true,
      order: 4,
      config: { helpText: 'ID of the accepted proposal that created this mission' },
    },
    {
      name: 'agentId',
      label: 'Agent',
      type: 'text',
      required: true,
      order: 5,
      config: { placeholder: 'Agent that owns this mission' },
    },
    {
      name: 'executingAgentId',
      label: 'Executing Agent',
      type: 'text',
      required: false,
      order: 6,
      config: { helpText: 'Agent assigned to execute this work (may differ from proposer)' },
    },
    {
      name: 'stepCount',
      label: 'Step Count',
      type: 'number',
      required: false,
      order: 7,
      config: { helpText: 'Total number of steps in this mission' },
    },
    {
      name: 'completedAt',
      label: 'Completed At',
      type: 'date',
      required: false,
      order: 8,
      config: {},
    },
  ],
}

/**
 * Seed schema for the mission_steps collection.
 * Concrete execution units within a mission, claimed and run by agents.
 */
export const missionStepsSchema: SeedSchema = {
  name: 'mission_steps',
  title: 'Mission Steps',
  description: 'Concrete execution units within a mission, claimed and run by agents',
  category: 'orchestration',
  target: 'records',
  titleField: 'title',
  orgId: SEED_ORG_PLACEHOLDER,
  workspaceId: SEED_WORKSPACE_PLACEHOLDER,
  builtIn: true,
  version: 2,
  archived: false,
  ownerPluginId: '',
  metadata: { idPrefix: 'mstp' },
  defaultSort: { field: 'createdAt', direction: 'asc' },
  fields: [
    {
      name: 'missionId',
      label: 'Mission',
      type: 'text',
      required: true,
      order: 1,
      config: { helpText: 'Parent mission ID' },
    },
    {
      name: 'title',
      label: 'Title',
      type: 'text',
      required: true,
      order: 2,
      config: { placeholder: 'Step title' },
    },
    {
      name: 'kind',
      label: 'Kind',
      type: 'select',
      required: true,
      order: 3,
      options: [
        { value: 'analyze', label: 'Analyze', color: '#6366f1' },
        { value: 'code_change', label: 'Code Change', color: '#3b82f6' },
        { value: 'draft', label: 'Draft', color: '#8b5cf6' },
        { value: 'review', label: 'Review', color: '#0ea5e9' },
        { value: 'deploy', label: 'Deploy', color: '#f97316' },
        { value: 'research', label: 'Research', color: '#a855f7' },
        { value: 'test', label: 'Test', color: '#22c55e' },
        { value: 'custom', label: 'Custom', color: '#6b7280' },
      ],
      config: { renderer: 'badge' },
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      required: true,
      defaultValue: 'queued',
      order: 4,
      options: [
        { value: 'queued', label: 'Queued', color: '#94a3b8' },
        { value: 'running', label: 'Running', color: '#f59e0b' },
        { value: 'succeeded', label: 'Succeeded', color: '#22c55e' },
        { value: 'failed', label: 'Failed', color: '#ef4444' },
      ],
      config: { renderer: 'badge' },
    },
    {
      name: 'payload',
      label: 'Payload',
      type: 'json',
      required: false,
      order: 5,
      config: { helpText: 'Input data for this step' },
    },
    {
      name: 'executingAgentId',
      label: 'Executing Agent',
      type: 'text',
      required: false,
      order: 6,
      config: { helpText: 'Agent assigned to execute this step (may differ from proposer)' },
    },
    {
      name: 'claimedBy',
      label: 'Claimed By',
      type: 'text',
      required: false,
      order: 7,
      config: { placeholder: 'Agent that claimed this step' },
    },
    {
      name: 'claimedAt',
      label: 'Claimed At',
      type: 'date',
      required: false,
      order: 8,
      config: {},
    },
    {
      name: 'result',
      label: 'Result',
      type: 'json',
      required: false,
      order: 9,
      config: { helpText: 'Output data from step execution' },
    },
    {
      name: 'ordinal',
      label: 'Order',
      type: 'number',
      required: false,
      order: 10,
      config: { helpText: 'Execution order within the mission' },
    },
  ],
}

/**
 * Seed schema for the agent_events collection.
 * Immutable event log for all agent activity across the orchestration system.
 */
export const agentEventsSchema: SeedSchema = {
  name: 'agent_events',
  title: 'Agent Events',
  description: 'Immutable event log for all agent activity in the orchestration system',
  category: 'orchestration',
  target: 'records',
  titleField: 'title',
  orgId: SEED_ORG_PLACEHOLDER,
  workspaceId: SEED_WORKSPACE_PLACEHOLDER,
  builtIn: true,
  version: 1,
  archived: false,
  ownerPluginId: '',
  metadata: { idPrefix: 'aevt' },
  defaultSort: { field: 'createdAt', direction: 'desc' },
  fields: [
    {
      name: 'agentId',
      label: 'Agent',
      type: 'text',
      required: true,
      order: 1,
      config: { placeholder: 'Agent identifier' },
    },
    {
      name: 'kind',
      label: 'Event Kind',
      type: 'select',
      required: true,
      order: 2,
      options: [
        { value: 'proposal_created', label: 'Proposal Created', color: '#6366f1' },
        { value: 'proposal_rejected', label: 'Proposal Rejected', color: '#ef4444' },
        { value: 'mission_created', label: 'Mission Created', color: '#3b82f6' },
        { value: 'mission_succeeded', label: 'Mission Succeeded', color: '#22c55e' },
        { value: 'mission_failed', label: 'Mission Failed', color: '#ef4444' },
        { value: 'step_claimed', label: 'Step Claimed', color: '#f59e0b' },
        { value: 'step_completed', label: 'Step Completed', color: '#22c55e' },
        { value: 'step_failed', label: 'Step Failed', color: '#ef4444' },
        { value: 'agent_circuit_break', label: 'Circuit Break', color: '#dc2626' },
        { value: 'coordinator_started', label: 'Coordinator Started', color: '#0ea5e9' },
        { value: 'trigger_fired', label: 'Trigger Fired', color: '#8b5cf6' },
      ],
      config: { renderer: 'badge' },
    },
    {
      name: 'title',
      label: 'Title',
      type: 'text',
      required: false,
      order: 3,
      config: { placeholder: 'Event summary' },
    },
    {
      name: 'payload',
      label: 'Payload',
      type: 'json',
      required: false,
      order: 4,
      config: { helpText: 'Arbitrary event data (proposalId, missionId, etc.)' },
    },
    {
      name: 'summary',
      label: 'Summary',
      type: 'richtext',
      required: false,
      order: 5,
      config: { placeholder: 'Detailed event description' },
    },
    {
      name: 'tags',
      label: 'Tags',
      type: 'json',
      required: false,
      order: 6,
      config: { helpText: 'Array of string tags for filtering and categorization' },
    },
    {
      name: 'relatedEntityId',
      label: 'Related Entity ID',
      type: 'text',
      required: false,
      order: 6,
      config: { helpText: 'ID of the related proposal, mission, or step' },
    },
    {
      name: 'relatedEntityType',
      label: 'Related Entity Type',
      type: 'select',
      required: false,
      order: 7,
      options: [
        { value: 'proposal', label: 'Proposal', color: '#f59e0b' },
        { value: 'mission', label: 'Mission', color: '#3b82f6' },
        { value: 'step', label: 'Step', color: '#0ea5e9' },
        { value: 'task', label: 'Task', color: '#22c55e' },
      ],
      config: { renderer: 'badge' },
    },
  ],
}

/**
 * Seed schema for the trigger_rules collection.
 * Declarative rules that create proposals when conditions are met.
 */
export const triggerRulesSchema: SeedSchema = {
  name: 'trigger_rules',
  title: 'Trigger Rules',
  description: 'Declarative rules that create proposals when conditions are met',
  category: 'orchestration',
  target: 'records',
  titleField: 'name',
  orgId: SEED_ORG_PLACEHOLDER,
  workspaceId: SEED_WORKSPACE_PLACEHOLDER,
  builtIn: true,
  version: 1,
  archived: false,
  ownerPluginId: '',
  metadata: { idPrefix: 'trul' },
  defaultSort: { field: 'name', direction: 'asc' },
  fields: [
    {
      name: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      order: 1,
      config: { placeholder: 'Rule name' },
    },
    {
      name: 'triggerType',
      label: 'Trigger Type',
      type: 'select',
      required: true,
      order: 2,
      options: [
        { value: 'reactive', label: 'Reactive', color: '#f59e0b' },
        { value: 'proactive', label: 'Proactive', color: '#6366f1' },
      ],
      config: { renderer: 'badge' },
    },
    {
      name: 'triggerEvent',
      label: 'Trigger Event',
      type: 'text',
      required: true,
      order: 3,
      config: { placeholder: 'Event name or cron expression' },
    },
    {
      name: 'conditions',
      label: 'Conditions',
      type: 'json',
      required: false,
      order: 4,
      config: { helpText: 'JSON conditions that must be satisfied for the rule to fire' },
    },
    {
      name: 'actionConfig',
      label: 'Action Config',
      type: 'json',
      required: true,
      order: 5,
      config: { helpText: 'Configuration for the proposal created when this rule fires' },
    },
    {
      name: 'cooldownMinutes',
      label: 'Cooldown (min)',
      type: 'number',
      required: true,
      order: 6,
      config: { helpText: 'Minimum minutes between firings' },
    },
    {
      name: 'enabled',
      label: 'Enabled',
      type: 'boolean',
      required: true,
      defaultValue: true,
      order: 7,
      config: {},
    },
    {
      name: 'fireCount',
      label: 'Fire Count',
      type: 'number',
      required: false,
      order: 8,
      config: { helpText: 'Number of times this rule has fired' },
    },
    {
      name: 'lastFiredAt',
      label: 'Last Fired At',
      type: 'date',
      required: false,
      order: 9,
      config: {},
    },
    {
      name: 'skipProbability',
      label: 'Skip Probability',
      type: 'number',
      required: false,
      order: 10,
      config: { helpText: 'Probability (0-1) of skipping a firing for randomization' },
    },
    {
      name: 'jitterMinutes',
      label: 'Jitter (min)',
      type: 'number',
      required: false,
      order: 11,
      config: { helpText: 'Random delay range in minutes added to scheduling' },
    },
    {
      name: 'intervalMinutes',
      label: 'Interval (min)',
      type: 'number',
      required: false,
      order: 12,
      config: { helpText: 'Recurring interval in minutes for proactive triggers' },
    },
  ],
}

/**
 * Seed schema for the ops_policy collection.
 * Runtime configuration stored as key-value pairs (configuration as data).
 */
export const opsPolicySchema: SeedSchema = {
  name: 'ops_policy',
  title: 'Ops Policy',
  description: 'Runtime configuration for the orchestration system stored as key-value pairs',
  category: 'orchestration',
  target: 'records',
  titleField: 'key',
  orgId: SEED_ORG_PLACEHOLDER,
  workspaceId: SEED_WORKSPACE_PLACEHOLDER,
  builtIn: true,
  version: 1,
  archived: false,
  ownerPluginId: '',
  metadata: { idPrefix: 'opol' },
  defaultSort: { field: 'key', direction: 'asc' },
  fields: [
    {
      name: 'key',
      label: 'Key',
      type: 'text',
      required: true,
      order: 1,
      config: { placeholder: 'Policy key (e.g., max_concurrent_missions)' },
    },
    {
      name: 'value',
      label: 'Value',
      type: 'json',
      required: true,
      order: 2,
      config: { helpText: 'Policy value (any JSON-serializable value)' },
    },
    {
      name: 'description',
      label: 'Description',
      type: 'richtext',
      required: false,
      order: 3,
      config: { placeholder: 'What this policy controls' },
    },
    {
      name: 'enabled',
      label: 'Enabled',
      type: 'boolean',
      required: true,
      defaultValue: true,
      order: 4,
      config: {},
    },
  ],
}

/**
 * Seed schema for the agent_affinities collection.
 * Pairwise relationship scores between agents that affect conversation dynamics.
 */
export const agentAffinitiesSchema: SeedSchema = {
  name: 'agent_affinities',
  title: 'Agent Affinities',
  description: 'Pairwise relationship scores between agents affecting conversation dynamics',
  category: 'orchestration',
  target: 'records',
  titleField: 'agentA',
  orgId: SEED_ORG_PLACEHOLDER,
  workspaceId: SEED_WORKSPACE_PLACEHOLDER,
  builtIn: true,
  version: 1,
  archived: false,
  ownerPluginId: '',
  metadata: { idPrefix: 'aaff' },
  defaultSort: { field: 'score', direction: 'desc' },
  fields: [
    {
      name: 'agentA',
      label: 'Agent A',
      type: 'text',
      required: true,
      order: 1,
      config: { helpText: 'Alphabetically first agent ID in the pair' },
    },
    {
      name: 'agentB',
      label: 'Agent B',
      type: 'text',
      required: true,
      order: 2,
      config: { helpText: 'Alphabetically second agent ID in the pair' },
    },
    {
      name: 'score',
      label: 'Affinity Score',
      type: 'number',
      required: true,
      order: 3,
      config: { helpText: 'Relationship score from 0.10 (antagonistic) to 0.95 (close allies)' },
    },
    {
      name: 'lastUpdated',
      label: 'Last Updated',
      type: 'date',
      required: true,
      order: 4,
      config: { helpText: 'ISO timestamp of last score change' },
    },
    {
      name: 'history',
      label: 'History',
      type: 'json',
      required: false,
      order: 5,
      config: { helpText: 'Last 10 score changes with drift, reason, and roundtableId' },
    },
  ],
}

/**
 * All orchestration seed schemas for the closed-loop agent system.
 */
export const orchestrationSeeds: SeedSchema[] = [
  proposalsSchema,
  missionsSchema,
  missionStepsSchema,
  agentEventsSchema,
  triggerRulesSchema,
  opsPolicySchema,
  agentAffinitiesSchema,
]
